<script setup lang="ts">
import { computed, watch } from 'vue'
import { useQuery } from '@/composables/api.ts'
import FMediaFile from '../../feature/FMediaFile/FMediaFile.vue'
import BTooltipBtn from '@/components/base/BTooltipBtn/BTooltipBtn.vue'
import type { Agreement, AgreementDetails } from './types'
import mdiFileSign from '~icons/mdi/file-sign'
import mdiOpenInNew from '~icons/mdi/open-in-new'

const props = defineProps<{
  agreement: Agreement
}>()

const emit = defineEmits<{
  'update:user-visible-data': [data: string]
}>()

const { data: agreementDetails, isLoading } = useQuery(
  '/agreements/{resource_id}',
  computed(() => ({
    path: { resource_id: props.agreement.id },
  }))
)

const agreementName = computed(() => {
  const details = agreementDetails.value as AgreementDetails | undefined
  if (details == null) return ''
  return details.creditor_name ?? details.account_name ?? ''
})

watch(
  agreementName,
  (name) => {
    if (name !== '') {
      emit('update:user-visible-data', name)
    }
  },
  { immediate: true }
)

const mediaFileReference = computed(() => ({
  id: props.agreement.bucket_id,
  filename: `${agreementName.value}.pdf`,
  content_type: 'application/pdf',
}))
</script>

<template>
  <v-col
    cols="12"
    class="mb-2"
  >
    <f-media-file
      resource="agreements"
      :resource-id="props.agreement.id"
      :file="mediaFileReference"
    >
      <template #activator="{ openDialog, href }">
        <v-card
          :loading="isLoading"
          variant="flat"
          link
          @click="openDialog"
        >
          <v-row
            align="center"
            class="pa-4"
            justify="space-between"
            no-gutters
          >
            <v-icon
              :icon="mdiFileSign"
              color="primary"
              size="large"
            />
            <v-col class="ml-3">
              <span
                v-if="agreementName"
                class="text-body-large"
              >
                {{ agreementName }}
              </span>
              <v-skeleton-loader
                v-else
                type="text"
                width="200"
              />
            </v-col>

            <b-tooltip-btn
              :icon="mdiOpenInNew"
              color="primary"
              variant="text"
              tooltip="open_in_new_tab"
              :href="href"
              target="_blank"
              @click.stop
            />
          </v-row>
        </v-card>
      </template>
    </f-media-file>
  </v-col>
</template>
